// React imports
import { type FC, useEffect, useMemo, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { useNavigate, useParams } from 'react-router-dom';

// Third-party imports
import { toast } from 'react-toastify';

// Component imports
import Loading from '@/components/Loading';
import CageCardTemplateWrapper from '@/components/Modals/PrintCageCards/CageCardTemplateWrapper';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import APITable from '@/components/UI/Table/Reusable/APITable';

// Utility imports
import type { CageApiId } from '@/model/Cage.model';
import type { StudyApiId } from '@/model/Study.model';
import { errorToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import { featuresSelector } from '@/support/Selectors';
import Http from '@/support/http';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { api as apiRoute, web as webRoute } from '@/support/route';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';

// Local imports
import {
  type Cage,
  colonyCagesBulkActions,
  generateColumns,
  generateFilterOptions,
  reducer,
  type CagesState,
} from './Cages.utils';

// TYPES AND INTERFACES

interface CagesProps {
  addToStudy?: boolean;
}

// CONSTANTS

const PAGE_SIZES = [50, 100, 200, 300];
const DEFAULT_SORT_BY = { id: 'id', desc: true };

const reasonForChangeSelector = createSelector([featuresSelector], (features) => ({
  reasonForChangeEnabled: features?.reason_for_change === true,
}));
const INCLUDE_PARAMS = 'counts,meta,study_link,operations';
const SEARCH_PLACEHOLDER = 'Search by Cage No or Name';
const LOADING_TEXT = 'Fetching cages...';

// MAIN COMPONENT

const Cages: FC<CagesProps> = ({ addToStudy = false }) => {
  // STATE INITIALIZATION AND HOOKS
  const initialState = useMemo(
    (): CagesState => ({
      updating: false,
      apiErrors: false,
      data: [],
      columns: [],
      searchQuery: '',
      filterOptions: generateFilterOptions({ fields: [], metadata: [] }, addToStudy),
      filters: [],
      filterMatch: true,
      selectedRows: {},
      total_pages: 1,
      current_page: 1,
      per_page: 10,
      order: 'desc' as const,
      sort: ['catalog'],
      cageIdToPrint: null,
      actions: [],
      metadata: [],
    }),
    [addToStudy]
  );

  const [state, dispatch] = useReducer(reducer, initialState);

  const { columns, updating, apiErrors, filterOptions, cageIdToPrint, actions, metadata } = state;

  // Redux hooks
  const storeDispatch = useDispatch();
  const { reasonForChangeEnabled } = useSelector(reasonForChangeSelector);
  const reduxTableName = addToStudy ? 'cagesAddToStudy' : 'colonyCages';

  const { studyId } = useParams();
  const navigate = useNavigate();

  const { invoke: deleteCages } = useApiHook({
    endpoint: 'DELETE /api/v1/cages/destroy-many',
    options: {
      invokeOnInit: false,
      onError: { throw: true, capture: true, toast: false, slug: 'colony-cages-delete-many' },
    },
  });

  const { invoke: updateCages } = useApiHook({
    endpoint: 'PATCH /api/v1/cages',
    options: {
      invokeOnInit: false,
      onError: { throw: true, capture: true, toast: false, slug: 'colony-cages-update-many' },
    },
  });

  // EFFECTS

  const assignCagesToStudy = async (selectedFlatRows: Array<{ original: { api_id: CageApiId; name: string } }>) => {
    dispatch({ type: 'SET_UPDATING', data: true });
    dispatch({ type: 'SET_ERROR', data: false });
    try {
      // The cage bulk-update endpoint identifies the target study by api_id, but the colony
      // add-to-study route only carries the numeric study id. Resolve it to the api_id first.
      // The legacy studies.show endpoint wraps its payload in a Fractal `data` envelope.
      const {
        data: { data: study },
      } = await Http.get<{ data: { api_id: StudyApiId } }>(apiRoute('studies.show', { id: studyId, include: '' }));

      const cages = selectedFlatRows.map(({ original: { api_id, name } }) => ({
        api_id,
        name,
        study_id: study.api_id,
      }));

      await updateCages({ body: { cages } });
      navigate(webRoute('studies.cages', { id: studyId }));
      successToast(`Successfully added ${cages.length > 1 ? 'cages' : 'cage'} to study`);
    } catch {
      dispatch({ type: 'SET_ERROR', data: true });
    } finally {
      dispatch({ type: 'SET_UPDATING', data: false });
    }
  };

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const { data } = await Http.get(apiRoute('cages.filterOptions'));
        dispatch({
          type: 'SET_FILTER_OPTIONS',
          data: generateFilterOptions(data, addToStudy),
        });

        // Create a stable version of handleRemoveCageFromStudy to break circular dependency
        const stableHandleRemoveCageFromStudy = async (apiId: CageApiId, name: string) => {
          dispatch({ type: 'SET_UPDATING', data: true });
          dispatch({ type: 'SET_ERROR', data: false });
          try {
            await updateCages({ body: { cages: [{ api_id: apiId, study_id: null }] } });
            storeDispatch({ type: 'CLOSE_MODAL' });
            toast.success(`Successfully removed ${name} from Study`);
          } catch {
            dispatch({ type: 'SET_ERROR', data: true });
          } finally {
            dispatch({ type: 'SET_UPDATING', data: false });
          }
        };

        dispatch({
          type: 'SET_COLUMNS',
          data: generateColumns(data, actions, addToStudy, dispatch, storeDispatch, stableHandleRemoveCageFromStudy),
        });
      } catch {
        dispatch({ type: 'SET_ERROR', data: true });
      }
    };

    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // EVENT HANDLERS AND FUNCTIONS

  /**
   * Handles deletion of multiple cages
   * @param selectedTableRows Array of cages to delete
   * @param callback Function to call on successful deletion
   */
  const handleDelete = async (
    selectedTableRows: Array<Cage>,
    callback: (message: string) => void,
    reasonForChange?: string
  ) => {
    try {
      await deleteCages({
        body: {
          cage_api_ids: selectedTableRows.map(({ api_id }) => api_id),
          ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
        },
      });
      return callback('Successfully deleted!');
    } catch {
      errorToast('There was a problem deleting the selected cages');
    }
  };

  /**
   * Renders custom actions for adding cages to study
   * @param param0 Object containing selected rows
   * @returns JSX element with add to study actions
   */
  const renderAddToStudyActions = ({
    selectedFlatRows,
  }: {
    selectedFlatRows: Array<{ original: { api_id: CageApiId; name: string } }>;
  }) => {
    if (addToStudy) {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      const disabled = !selectedFlatRows.length;
      return (
        <div className="flex pa1 mt4 mb2 justify-start">
          <Button
            disabled={disabled}
            tooltip={disabled ? 'Please select at least one cage to add to the study' : undefined}
            onClick={() => assignCagesToStudy(selectedFlatRows)}
          >
            Assign
          </Button>
          <Button className="ml2" plain url={webRoute('studies.cages', { id: studyId })}>
            Cancel
          </Button>
        </div>
      );
    }
  };

  const bulkActions = (tableProps: {
    useTableProps: { selectedFlatRows: Array<{ original: Cage }>; apiTable: { refetchTableData: () => Promise<void> } };
  }) =>
    !addToStudy
      ? colonyCagesBulkActions(tableProps, handleDelete, storeDispatch, navigate, reasonForChangeEnabled)
      : [];

  // OR-2531: When adding cages to a study with colony, filter out cages already in one.
  const filterOutStudy = {
    type: 'and',
    op: 'eq',
    filter: 'study',
    value: null,
  };

  const loadingTxt = LOADING_TEXT;

  return (
    <>
      <div className="ma4">
        {apiErrors && (
          <Banner critical dismiss={false} className="mw6 ma3">
            <h3 className="f5 normal lh-title pb2">There was an error fetching cages</h3>
            <p className="f6 pb3 lh-copy">If this error persists try again later or contact support.</p>
            <Button outline critical url={'mailto:support@benchling.com'}>
              Contact support
            </Button>
          </Banner>
        )}
        <div className={`cages ${updating ? 'ui__disabled' : ''}`}>
          {updating ? (
            <div style={{ height: 530 }}>
              <Loading txt={loadingTxt} />
            </div>
          ) : (
            <APITable
              apiInfo={{
                type: 'legacyInternalApi',
                route: apiRoute('cages.index'),
              }}
              customFilter={addToStudy ? filterOutStudy : null}
              includeParam={INCLUDE_PARAMS}
              defaultSortBy={DEFAULT_SORT_BY}
              columns={columns as any}
              customFilterProcessor={createMultiSelectDoubleEncodingProcessor(
                metadata as Array<{ id: string | number; field_type: string }>
              )}
              bulkActions={bulkActions}
              showViewSelector={!addToStudy}
              filterOptions={filterOptions}
              searchPlaceholderText={SEARCH_PLACEHOLDER}
              pageSizes={PAGE_SIZES}
              renderCustomActions={addToStudy ? renderAddToStudyActions : undefined}
              reduxTableName={reduxTableName}
              loadingText={loadingTxt}
              data-testid="cages-api-table"
            />
          )}
        </div>
      </div>
      {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
      {cageIdToPrint && (
        <CageCardTemplateWrapper
          cageId={cageIdToPrint}
          onComplete={() => dispatch({ type: 'SET_CAGE_ID_TO_PRINT', data: null })}
        />
      )}
    </>
  );
};

export default Cages;
